import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { PDKEditIcon } from '@/components/ui/pdk-icons';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import useHoverOrOpenShow from '@/components/ux/hooks/use-hover-or-open-show';
import { useGetDealReplyUrl } from '@/hooks/use-get-deal-reply-url';
import { toast } from '@/hooks/use-toast';
import { useUser } from '@/hooks/use-user';
import { cn } from '@/lib/utils';
import type { Reply } from '@/models/registry';
import { Ellipsis, LinkIcon } from 'lucide-react';
import { useState } from 'react';

const ReplyMenu = ({
  reply,
  dealId,
  hoverRef,
  setIsEditingReply,
}: {
  reply: Reply;
  dealId: string;
  hoverRef: React.RefObject<HTMLDivElement | null>;
  setIsEditingReply: (isEditing: boolean) => void;
}) => {
  const dealReplyUrl = useGetDealReplyUrl(dealId, reply);
  const [open, setOpen] = useState(false);

  const { shouldShow: shouldShowMenu } = useHoverOrOpenShow({
    hoverGroupRef: hoverRef,
    open,
  });

  const user = useUser();

  const replyBelongsToUser =
    reply.created_by && reply.created_by.id === user.id;

  return (
    <div
      className={cn({
        'opacity-0': !shouldShowMenu,
        'opacity-100': shouldShowMenu,
      })}
    >
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <div>
            <Tooltip delayDuration={1000}>
              <TooltipTrigger asChild>
                <Button variant="ghost" className="p-1 h-fit">
                  <Ellipsis className={cn('h-3.5 w-3.5 cursor-pointer', {})} />
                </Button>
              </TooltipTrigger>
              <TooltipContent className="flex flex-row gap-1 items-center">
                Reply Actions Menu
              </TooltipContent>
            </Tooltip>
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
          align="start"
          side="bottom"
          sideOffset={4}
        >
          {replyBelongsToUser && (
            <DropdownMenuItem
              onClick={() => {
                setIsEditingReply(true);
                setOpen(false);
              }}
            >
              <PDKEditIcon className="size-4 cursor-pointer" />
              <div>Edit</div>
            </DropdownMenuItem>
          )}

          <DropdownMenuItem
            className="flex flex-row gap-2 "
            onClick={(e: React.MouseEvent<HTMLDivElement>) => {
              e.preventDefault();
              e.stopPropagation();

              if (!dealReplyUrl) {
                toast({
                  title: 'Sorry no url to reply could be generated',
                });
                return;
              }

              navigator.clipboard.writeText(dealReplyUrl);

              toast({
                title: 'Link to reply copied to clipboard',
              });
            }}
          >
            <LinkIcon className="size-4 cursor-pointer" />
            <div>Copy link to reply</div>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default ReplyMenu;
